import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cl from './AnimMovie.module.css';
import StudioAnim from './StudioAnim';
/* Озвучка — общая для всего учебного слоя редактора (Silero школы
   с откатом на браузерный синтез). Живёт уровнем выше, потому что ею
   пользуется не только этот плеер. */
import { speakAsync, stopSpeaking, preloadVoices } from '../speech';

/**
 * AnimMovie — проигрыватель теории на анимациях.
 *
 * Пришёл на смену комикс-движку: вместо листа с нарисованными кадрами
 * показываем ОДИН кадр во весь экран, внутри которого живёт анимация
 * интерфейса Студии (StudioAnim). Кадр держится столько, сколько диктор
 * читает титр — как в комиксе, но без статичных картинок.
 *
 * Управление: пауза, шаг назад/вперёд, скорость, точки прогресса.
 * Клик по точке — перейти к кадру: ученик должен мочь пересмотреть момент,
 * который не понял, не отсматривая всё заново.
 *
 * Props:
 *   scene    — { kind: 'anim', frames: [{ say, anim }] }
 *   onFinish — вызывается по завершении
 */

const RATES = [0.75, 1, 1.25, 1.5];

/**
 * Принудительная пауза в конце кадра — «усвоить произошедшее».
 * Не полагаемся на кнопку паузы: исследования показывают, что ученики
 * пересматривают лишь 6.7% времени, а 20% видео пропускают совсем.
 */
const END_PAUSE_MS = 900;

export default function AnimMovie({ scene, onFinish }) {
    /* useMemo: без него массив пересоздаётся каждый рендер, из-за чего
       runFrame пересобирается и кадр может перезапуститься сам собой. */
    const frames = useMemo(() => scene?.frames || [], [scene]);
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [rate, setRate] = useState(1);
    const [preloading, setPreloading] = useState(true);

    const speakRef = useRef(null);
    const timerRef = useRef(null);
    const tokenRef = useRef(0);

    // Предзагрузка озвучки: без неё первые кадры идут молча, пока
    // подтягивается звук, и прогресс расходится с речью.
    useEffect(() => {
        let cancelled = false;
        preloadVoices(frames.map((f) => f.say || ''))
            .finally(() => { if (!cancelled) setPreloading(false); });
        // Не ждём вечно — если TTS недоступен, начинаем без него.
        const t = setTimeout(() => { if (!cancelled) setPreloading(false); }, 5000);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearTimer = () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };

    /**
     * Проиграть кадр: озвучить титр и перейти к следующему.
     *
     * ⭐ ТАЙМИНГ ОТ ОЗВУЧКИ (модель Duolingo): длительность кадра НЕ
     * проставляется руками, а берётся из речи. Именно эту задачу автор Manim
     * признал у себя нерешённой — там клипы монтируются вручную.
     *
     * Кадр держится столько, сколько идёт РЕЧЬ, но не меньше, чем длится
     * анимация: иначе ученик не увидит, чем кончилось действие. Плюс
     * принудительная пауза в конце — ученики сами паузу почти не жмут
     * (пересматривают 6.7% времени, 20% видео пропускают совсем).
     */
    const runFrame = useCallback((i) => {
        const token = ++tokenRef.current;
        const fr = frames[i];
        if (!fr) return;

        // Сколько длится анимация кадра — кадр не может кончиться раньше.
        const animMs = (fr.steps || []).reduce((s, st) => s + (st.dur || 300), 0);
        const started = performance.now();

        speakRef.current?.cancel();
        const handle = speakAsync(fr.say || '', rate);
        speakRef.current = handle;

        handle.promise.catch(() => {}).then(() => {
            if (token !== tokenRef.current) return;

            // Речь кончилась. Если анимация ещё идёт — ждём её.
            const elapsed = performance.now() - started;
            const restOfAnim = Math.max(0, animMs / rate - elapsed);
            const holdMs = restOfAnim + END_PAUSE_MS / rate;

            timerRef.current = setTimeout(() => {
                if (token !== tokenRef.current) return;
                if (i < frames.length - 1) setIdx(i + 1);
                else { setPlaying(false); onFinish?.(); }
            }, holdMs);
        });
    }, [frames, rate, onFinish]);

    useEffect(() => {
        if (preloading) return undefined;
        if (!playing) { speakRef.current?.cancel(); clearTimer(); tokenRef.current++; return undefined; }
        runFrame(idx);
        return () => { clearTimer(); };
    }, [idx, playing, preloading, runFrame]);

    useEffect(() => () => { stopSpeaking(); clearTimer(); }, []);

    const goto = (i) => {
        tokenRef.current++;
        clearTimer();
        speakRef.current?.cancel();
        setIdx(Math.max(0, Math.min(frames.length - 1, i)));
    };

    if (!frames.length) return null;

    if (preloading) {
        return (
            <div className={cl.wrap}>
                <div className={cl.loading}>
                    <span className={cl.spinner} />
                    Готовим урок…
                </div>
            </div>
        );
    }

    const fr = frames[idx];

    return (
        <div className={cl.wrap}>
            <div className={cl.stage}>
                {/* key — чтобы анимация перезапускалась при смене кадра.
                    Кадры 'idea' (объяснение) статичны: анимация концепций
                    не помогает обучению, помогает только анимация действий. */}
                <StudioAnim
                    key={idx}
                    spec={{ steps: fr.steps || [], layout: fr.layout, diagram: fr.diagram, labels: fr.labels }}
                    active={playing && fr.kind !== 'idea'}
                    still={fr.kind === 'idea'}
                />
            </div>

            <div className={cl.caption}>{fr.say}</div>

            <div className={cl.bar}>
                <button
                    className={cl.ctrl}
                    onClick={() => goto(idx - 1)}
                    disabled={idx === 0}
                    title="Предыдущий кадр"
                >‹</button>

                <button
                    className={`${cl.ctrl} ${cl.play}`}
                    onClick={() => setPlaying((p) => !p)}
                    title={playing ? 'Пауза' : 'Продолжить'}
                >{playing ? '❚❚' : '▶'}</button>

                <button
                    className={cl.ctrl}
                    onClick={() => goto(idx + 1)}
                    disabled={idx === frames.length - 1}
                    title="Следующий кадр"
                >›</button>

                <div className={cl.dots}>
                    {frames.map((_, i) => (
                        <button
                            key={i}
                            className={`${cl.dot} ${i === idx ? cl.dotOn : ''} ${i < idx ? cl.dotDone : ''}`}
                            onClick={() => goto(i)}
                            title={`Кадр ${i + 1}`}
                        />
                    ))}
                </div>

                <span className={cl.counter}>{idx + 1} / {frames.length}</span>

                <button
                    className={cl.rate}
                    onClick={() => setRate(RATES[(RATES.indexOf(rate) + 1) % RATES.length])}
                    title="Скорость"
                >{rate}×</button>
            </div>
        </div>
    );
}
